import { z } from 'zod';
export const severitySchema = z.enum(['INFO', 'WARNING', 'BLOCKER']);
export const ruleResultSchema = z.object({
    id: z.string(),
    severity: severitySchema,
    title: z.string(),
    description: z.string(),
    evidence: z.string(),
    remediation: z.string(),
    fingerprint: z.string().regex(/^[a-f0-9]{64}$/, 'Must be 64-char hex'),
});
export const policyOutputMetadataSchema = z.object({
    applied: z.literal(true),
    source: z.literal('file'),
    path: z.string(),
    schemaVersion: z.literal(1),
    effective: z.object({
        defaultEnabled: z.boolean(),
        failOnThreshold: severitySchema,
    }),
});
export const baselineOutputMetadataSchema = z.object({
    path: z.string(),
    findingsCount: z.number().int().nonnegative(),
    diff: z.object({
        new: z.number().int().nonnegative(),
        resolved: z.number().int().nonnegative(),
        unchanged: z.number().int().nonnegative(),
    }),
});
export const scanReportSchema = z.object({
    platform: z.enum(['ios', 'android']),
    artifactKind: z.enum([
        'ios_xcarchive',
        'ios_ipa',
        'ios_app',
        'android_aab',
        'android_apk',
    ]),
    appId: z.string().optional(),
    scanVersion: z.string(),
    coverage: z.object({
        full: z.boolean(),
        notes: z.array(z.object({ ruleId: z.string().optional(), message: z.string() })),
    }),
    results: z.array(ruleResultSchema),
    policy: policyOutputMetadataSchema.optional(),
    baseline: baselineOutputMetadataSchema.optional(),
});
// ============================================================================
// Aggregate Report Schemas
// ============================================================================
const coverageNoteSchema = z.object({
    ruleId: z.string().optional(),
    message: z.string(),
});
const severityCountsSchema = z.object({
    BLOCKER: z.number().int().nonnegative(),
    WARNING: z.number().int().nonnegative(),
    INFO: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
export const aggregateReportItemSchema = z.object({
    key: z.string(),
    platform: z.enum(['ios', 'android']),
    artifactKind: z.enum([
        'ios_xcarchive',
        'ios_ipa',
        'ios_app',
        'android_aab',
        'android_apk',
    ]),
    appId: z.string().optional(),
    scanVersion: z.string(),
    coverage: z.object({
        full: z.boolean(),
        notes: z.array(coverageNoteSchema),
    }),
    results: z.array(ruleResultSchema),
    counts: severityCountsSchema,
});
export const aggregateReportSchema = z.object({
    schemaVersion: z.literal(1),
    aggregateVersion: z.string(),
    items: z.array(aggregateReportItemSchema),
    totals: z.object({
        items: z.number().int().nonnegative(),
        findings: severityCountsSchema,
        byRuleId: z.array(z.object({
            ruleId: z.string(),
            severity: severitySchema,
            count: z.number().int().nonnegative(),
        })),
        coverageNotes: z.array(coverageNoteSchema),
    }),
});
